import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Filter, ArrowLeft, Phone, Mail, Music, Star, Pencil, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Field, inputCls, StatusBadge } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const INSTRUMENTS = ["Vocals", "Lead Vocals", "BVs", "Piano", "Keys", "Acoustic Guitar", "Electric Guitar", "Bass", "Drums", "Percussion", "Strings", "Other"];
const VOCAL_PARTS = ["Soprano", "Alto", "Tenor", "Baritone", "Other"];
const ROLES = ["Worship Leader", "Worship Director", "Musician", "Vocalist", "Bass", "Drums", "Tech / Sound"];
const EXP = ["Beginner", "Intermediate", "Advanced"];

function Avatar({ name, size = 48 }) {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="rounded-full border-2 border-[#E5C378]/40 bg-[#18233C] flex items-center justify-center font-bold text-[#E5C378]"
      style={{ width: size, height: size, fontSize: size / 2.6 }}>{initials}</div>
  );
}

function MemberDialog({ member, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const empty = { first_name: "", last_name: "", email: "", phone: "", role: "Musician", instrument: "Vocals", vocal_part: "Other", experience: "Intermediate", key_range: "", notes: "", secondary_instruments: [] };
  const [f, setF] = useState(member ? { ...empty, ...member } : empty);
  useEffect(() => { if (member) setF({ ...empty, ...member }); /* eslint-disable-next-line */ }, [member, open]);
  const save = async () => {
    if (!f.first_name) { toast.error("First name is required."); return; }
    try {
      if (member) { await api.put(`/team/${member.id}`, f); toast.success("Member updated"); }
      else { await api.post("/team", f); toast.success("Member added"); }
      setOpen(false); onSaved(); if (!member) setF(empty);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <button data-testid="add-member-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl wf-gold-btn font-semibold text-sm"><Plus className="w-4 h-4" /> Add member</button>}
      </DialogTrigger>
      <DialogContent className="wf-glass border-white/10 max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">{member ? "Edit member" : "New team member"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><input className={inputCls} data-testid="member-first-name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} /></Field>
          <Field label="Last name"><input className={inputCls} value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} /></Field>
          <Field label="Email"><input className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Phone"><input className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
          <Field label="Role"><select className={inputCls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select></Field>
          <Field label="Primary instrument"><select className={inputCls} value={f.instrument} onChange={(e) => setF({ ...f, instrument: e.target.value })}>{INSTRUMENTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
          <Field label="Vocal part"><select className={inputCls} value={f.vocal_part} onChange={(e) => setF({ ...f, vocal_part: e.target.value })}>{VOCAL_PARTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
          <Field label="Experience"><select className={inputCls} value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })}>{EXP.map((r) => <option key={r}>{r}</option>)}</select></Field>
          <Field label="Preferred key range"><input className={inputCls} placeholder="C–G" value={f.key_range} onChange={(e) => setF({ ...f, key_range: e.target.value })} /></Field>
        </div>
        <div className="mt-3"><Field label="Description / notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="member-notes" placeholder="e.g. Lead keys + acoustic; available most Sundays" /></Field></div>
        <button onClick={save} data-testid="save-member-button" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm mt-2">{member ? "Save changes" : "Save member"}</button>
      </DialogContent>
    </Dialog>
  );
}

export function Team() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [instrument, setInstrument] = useState("");
  const [role, setRole] = useState("");
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);

  const load = async () => {
    const params = new URLSearchParams();
    if (instrument) params.set("instrument", instrument);
    if (role) params.set("role", role);
    const { data } = await api.get(`/team?${params}`);
    setMembers(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [instrument, role]);

  return (
    <div>
      <PageHeader eyebrow="The people" title="Worship Team Directory"
        subtitle="Every voice and instrument that serves your gatherings."
        actions={canManage && <MemberDialog onSaved={load} />} />

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="flex items-center gap-1.5 text-xs text-[#8A97AC]"><Filter className="w-3.5 h-3.5" /> Filter:</div>
        <select className="bg-[#0B101D] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-[#F7F5F0]" value={instrument} onChange={(e) => setInstrument(e.target.value)} data-testid="filter-instrument">
          <option value="">All instruments</option>{INSTRUMENTS.map((i) => <option key={i}>{i}</option>)}
        </select>
        <select className="bg-[#0B101D] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-[#F7F5F0]" value={role} onChange={(e) => setRole(e.target.value)} data-testid="filter-role">
          <option value="">All roles</option>{ROLES.map((i) => <option key={i}>{i}</option>)}
        </select>
      </div>

      {members.length === 0 ? (
        <EmptyState icon={Music} title="No members yet" body="Add your first worship team member to begin building teams and rosters." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((m) => (
            <button key={m.id} data-testid={`member-card-${m.id}`} onClick={() => nav(`/team/${m.id}`)}
              className="wf-card rounded-xl p-4 flex items-center gap-4 text-left transition-all hover:-translate-y-0.5">
              <Avatar name={m.name} />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[#F7F5F0] truncate">{m.name}</div>
                <div className="text-xs text-[#E5C378]">{m.role}</div>
                <div className="text-xs text-[#8A97AC] mt-1 truncate">{[m.instrument, ...(m.secondary_instruments || [])].filter(Boolean).join(" · ")}</div>
              </div>
              {!m.active && <StatusBadge status="draft" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MemberProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);
  const load = () => api.get(`/team/${id}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const removeMember = async () => {
    if (!window.confirm("Remove this member? This can't be undone.")) return;
    try { await api.delete(`/team/${id}`); toast.success("Member removed"); nav("/team"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!data) return <div className="text-[#8A97AC]">Loading…</div>;
  const m = data.member;
  return (
    <div>
      <button onClick={() => nav("/team")} className="flex items-center gap-1.5 text-sm text-[#8A97AC] hover:text-[#E5C378] mb-4"><ArrowLeft className="w-4 h-4" /> Team</button>
      <div className="wf-card rounded-2xl p-6 flex flex-col sm:flex-row gap-5 items-start mb-6">
        <Avatar name={m.name} size={72} />
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-[#F7F5F0]">{m.name}</h1>
          <div className="text-[#E5C378]">{m.role}</div>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#8A97AC]">
            {m.email && <span className="flex items-center gap-1.5"><Mail className="w-4 h-4" /> {m.email}</span>}
            {m.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> {m.phone}</span>}
          </div>
          {m.notes && <p className="text-sm text-[#B8C1D1] mt-3 max-w-lg">{m.notes}</p>}
        </div>
        {canManage && (
          <div className="flex gap-2">
            <MemberDialog member={m} onSaved={load} trigger={<button data-testid="edit-member-button" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] text-sm"><Pencil className="w-4 h-4" /> Edit</button>} />
            <button onClick={removeMember} data-testid="delete-member-button" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm"><Trash2 className="w-4 h-4" /> Remove</button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="wf-card rounded-2xl p-5">
          <h3 className="font-display font-semibold text-[#F7F5F0] mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-[#E5C378]" /> Skills</h3>
          <div className="space-y-2 text-sm">
            <Row k="Primary" v={m.instrument} />
            <Row k="Secondary" v={(m.secondary_instruments || []).join(", ") || "—"} />
            <Row k="Vocal part" v={m.vocal_part} />
            <Row k="Experience" v={m.experience} />
            <Row k="Key range" v={m.key_range || "—"} />
          </div>
        </div>
        <div className="wf-card rounded-2xl p-5">
          <h3 className="font-display font-semibold text-[#F7F5F0] mb-3">Upcoming services</h3>
          {data.upcoming.length === 0 ? <p className="text-sm text-[#6E7D97]">None scheduled.</p> :
            data.upcoming.map((s) => (
              <button key={s.id} onClick={() => nav(`/services/${s.id}`)} className="w-full flex items-center justify-between py-2 border-b border-white/5 text-left">
                <span className="text-sm text-[#F7F5F0]">{s.title}<span className="text-xs text-[#8A97AC] block">{s.date}</span></span>
                <StatusBadge status={s.status} />
              </button>
            ))}
        </div>
        <div className="wf-card rounded-2xl p-5">
          <h3 className="font-display font-semibold text-[#F7F5F0] mb-3">Songs played</h3>
          {data.songs_played.length === 0 ? <p className="text-sm text-[#6E7D97]">No history yet.</p> :
            <div className="flex flex-wrap gap-2">{data.songs_played.map((s) => <span key={s} className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-[#B8C1D1]">{s}</span>)}</div>}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-[#8A97AC]">{k}</span><span className="text-[#F7F5F0] font-medium">{v}</span></div>;
}
